"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useState } from "react";
import ChartOne from "../Charts/ChartOne";
import ChartTwo from "../Charts/ChartTwo";
import ChatCard from "../Chat/ChatCard";
import TableOne from "../Tables/TableOne";
import CardDataStats from "../CardDataStats";
import * as geoappui from "ui";
import { PaginatedList } from "../Common/Pagination";
import LocationSelector from "../Location/Selector";
import { LocalSearchHeader } from "../LocalSearch/header";
import SearchResults from "../LocalSearch/results";
import ContentCard from "../Cards/Content";
import { GroupProfile } from "../Group/profile";
import { TabBar } from "../TabBar";
import { useSearchParams, useRouter } from 'next/navigation';
import { useLocation } from "../Location/Context";
import "mapbox-gl/dist/mapbox-gl.css"; // Import Mapbox styles
import BackButton from "../BackButton";
import { GetAnonymousViewMapDataQueryVariables, useGetAnonGeoActivityByActivityIdLazyQuery, useGetAnonymousViewMapDataLazyQuery, useGetAnonGeoCodeByGeoCodeIdLazyQuery } from "@/generated/public/graphql";
import useSharedState from "@/hooks/useSharedState";
import { ApiClient } from "@/utils/apiClient";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ListMapToggle } from "../Buttons/ToggleListMap";
import { FiEdit } from "react-icons/fi";
import axios from "axios";
import { buildQueryString } from "@/utils/links";
import { useConfig } from "@/context/ConfigContext";
import moment from 'moment-timezone';
import { getUserLocation } from "@/utils/ipAddress";
import debounce from "lodash/debounce";
const defaultSimpleLocation = "Manhattan, New York, NY, USA"; // default
const defaultSimpleLocationLatLng = {
  lng: -73.935242,
  lat: 40.730610
};
const dateTimeToPriority = (dateTimeStr: string): number => {
  const date = new Date(dateTimeStr);
  // Return the Unix timestamp (in seconds)
  return Math.floor(date.getTime() / 1000);
};
const isInputDatePastNow = (inputString: string): boolean => {
  const localTz = moment.tz.guess(); // Get user's current timezone

  // Remove timezone from input and set it to the local timezone
  const inputWithoutTz = moment.parseZone(inputString).format("YYYY-MM-DDTHH:mm:ss");
  const inputWithLocalTz = `${inputWithoutTz}${moment().tz(localTz).format("Z")}`;

  // Parse both dates as local times for comparison
  const inputDate = moment(inputWithLocalTz);
  const currentDate = moment().local();

  // console.log({
  //   inputString,
  //   inputDate: inputDate.format(),
  //   currentDate: currentDate.format(),
  // })

  return inputDate.isBefore(currentDate);
};
interface LocationMarkerIcon {
  url: number;
  name: string;
}
interface LocationMarker {
  type: string;
  id: number;
  title: string;
  description: string;
  lng: number; // Add longitude
  lat: number; // Add latitude
  location?: string;
  eventDate?: string | null;
  eventTime?: string | null;
  eventTimestamp?: string | null;
  priority?: number;
  // metaJson?: any
  likes?: number;
  comments?: number;
  views?: number;
  media?: Array<{
    type: 'image' | 'video' | 'gallery';
    url: string[];
  }>;
  icon?: LocationMarkerIcon;
  refUrl?: string | null;
  searchable?: string[];
}
const MapOne = dynamic(() => import("@/components/Maps/MapOne"), {
  ssr: false
});
const ChartThree = dynamic(() => import("@/components/Charts/ChartThree"), {
  ssr: false
});
const LocalSearch: React.FC = () => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const config = useConfig();
  // console.log("config:::", config)
  // console.log({
  //   isMobile
  // })
  const didMountRef = React.useRef(false);
  const mapRef = React.useRef<mapboxgl.Map | null>(null);
  const {
    location: searchLocation,
    setLocation: setSearchLocation
  } = useLocation();
  const searchParams = useSearchParams();
  const [params, setParams] = useState(new URLSearchParams(searchParams.toString()));
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    // console.log("Only runs after mount, when deps change");
  }, []);
  const hasAnyValue = (...deps: any[]) => deps.some(dep => !!dep);
  const setSearchParams = (newParams: URLSearchParams) => {
    if (params.toString() != newParams.toString()) {
      setParams(newParams);
      const pushHistory = !!newParams.get('activityId') || !!newParams.get('placeId');
      if (pushHistory) {
        router.push(`?${newParams.toString()}`, {
          scroll: false
        });
      } else {
        router.replace(`?${newParams.toString()}`, {
          scroll: false
        });
      }
    }
  };
  const clearParam = (param: string | string[]) => {
    // console.log("clearParam called: " + param)
    const newParams = new URLSearchParams(params.toString());
    if (Array.isArray(param)) {
      param.forEach(p => newParams.delete(p));
    } else {
      newParams.delete(param);
    }
    setSearchParams(newParams);
  };
  const setParam = (param: string, value: string) => {
    // console.log("setParam called: " + param)
    // Set a single query param
    const newParams = new URLSearchParams(params.toString());
    newParams.set(param, value);
    setSearchParams(newParams);
  };
  const dropIdParam = params.get('dropId');
  const castIdParam = params.get('castId');
  const activityIdParam = params.get('activityId');
  const placeIdParam = params.get('placeId');
  const searchParam = params.get('search');
  const locationIdParam = params.get('location');
  const [place, setPlace] = useState<any>();
  const [placeId, setPlaceId] = useState<any>(placeIdParam);
  // TODO: Use JIT decrption of the placeId value - let's encode the google:sdfdsf so that people don't know w'ere using google place ids directly.
  const [activityId, setActivityId] = useState<any>(dropIdParam || castIdParam || activityIdParam);
  const [locationIdParamLast, setLocationIdParamLast] = useState<string>();
  const [showProfileSection, setShowProfileSection] = useState<boolean>(false);
  const [activity, setActivity] = useState<any>();
  const [displayedResults, setDisplayedResults] = useState<any>();
  const lastSerializedDataRef = React.useRef<string | null>(null);
  const lastSerializedRequestRef = React.useRef<string | null>(null);
  const lastSerializedParamRef = React.useRef<URLSearchParams | null>(null);
  const [selectedIcon, setSelectedIcon] = useSharedState('selectedIcon', null);
  const [isMapMode, setIsMapMode] = useState(false);
  const [getAnonymousViewMapData, {
    loading,
    error,
    data,
    refetch
  }] = useGetAnonymousViewMapDataLazyQuery({
    fetchPolicy: 'cache-and-network'
  });
  const [loadAnonGeoCode, {
    error: errorPlace,
    loading: loadingPlace,
    data: dataPlace
  }] = useGetAnonGeoCodeByGeoCodeIdLazyQuery({
    fetchPolicy: 'cache-and-network'
  });
  const [getAnonGeoActivityByActivityId, {
    loading: loadingActivity,
    error: errorActivity,
    data: dataActivity
  }] = useGetAnonGeoActivityByActivityIdLazyQuery({
    fetchPolicy: 'cache-and-network'
  });
  function getMapBoundaries() {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const southWest = bounds.getSouthWest();
        const northEast = bounds.getNorthEast();
        // Map the result to match the required structure
        const result = {
          left: southWest.lat,
          // Latitude of southwest corner (matches left)
          bottom: southWest.lng,
          // Longitude of southwest corner (matches bottom)
          right: northEast.lat,
          // Latitude of northeast corner (matches right)
          top: northEast.lng // Longitude of northeast corner (matches top)
        };
        // console.log("getMapBoundaries:", result)
        return result;
      }
    }
  }
  function formatDateRange(startDate: string, endDate?: string): {
    eventDate: string;
    eventTime: string;
  } {
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      // 'Thu'
      year: 'numeric',
      // '2024'
      month: 'short',
      // 'Oct'
      day: 'numeric' // '3'
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      // '7 PM'
      minute: 'numeric' // '00'
    };
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    const eventDate = start.toLocaleDateString(undefined, dateOptions);
    const startTime = start.toLocaleTimeString(undefined, timeOptions);
    let eventTime: string;
    if (!endDate || start.toDateString() === end?.toDateString()) {
      // Same day or no end date
      const endTime = end ? end.toLocaleTimeString(undefined, timeOptions) : null;
      eventTime = endTime && startTime !== endTime ? `${startTime} - ${endTime}` : `${startTime}`;
    } else {
      // Different day
      const endTime = end!.toLocaleTimeString(undefined, timeOptions);
      const endDateDisplay = end!.toLocaleDateString(undefined, dateOptions);
      eventTime = `${startTime} - ${endTime} (${endDateDisplay})`;
    }
    return {
      eventDate,
      eventTime
    };
  }
  const mapDropToLocationMarker = (drop: any) => {
    const {
      eventDate,
      eventTime
    } = formatDateRange(drop?.displayStartDate, drop?.displayEndDate);
    const mediaData: {
      type: 'image' | 'video' | 'gallery';
      url: string[];
    }[] = [];
    if (drop?.refImageUrl) {
      mediaData.push({
        type: 'image',
        url: [drop?.refImageUrl]
      });
    }
    if (drop?.refVideoUrl) {
      mediaData.push({
        type: 'video',
        url: [drop?.refVideoUrl]
      });
    }
    const proxyIconUrl = `/api/proxy-image?url=${encodeURIComponent(drop?.appDropCategory?.iconUrl)}`;
    return {
      id: drop?.id,
      type: 'drop',
      isEvent: drop?.appDropCategory?.isEvent,
      title: drop?.messageSubject,
      description: drop?.messageBody,
      lat: drop?.geocode?.locationCoordinates?.latitude,
      lng: drop?.geocode?.locationCoordinates?.longitude,
      location: drop?.geocode?.name,
      eventDate: eventDate,
      eventTime: eventTime,
      eventTimestamp: drop?.displayStartDate,
      priority: dateTimeToPriority(drop?.displayStartDate),
      refUrl: drop?.refUrl,
      searchable: [drop?.messageSubject, drop?.messageBody, `icon:${drop?.appDropCategory?.name}`],
      media: mediaData,
      // metaJson: drop?.metaJson,
      views: drop?.metaJson?.track?.views || 0,
      likes: drop?.likeCount || 0,
      comments: drop?.commentCount || 0,
      icon: {
        url: proxyIconUrl as any,
        name: drop?.appDropCategory?.name
      }
    };
  };
  const mapPlaceToLocationMarker = (place: any) => {
    // const { eventDate, eventTime } = formatDateRange(drop?.displayStartDate, drop?.displayEndDate)
    const mediaData: {
      type: 'image' | 'video' | 'gallery';
      url: string[];
    }[] = [];
    if (place?.placeInfoImageUrl) {
      mediaData.push({
        type: 'image',
        url: [place?.placeInfoImageUrl]
      });
      // loop: placeInfoImageGallery
    }
    let placeCategoryIconPrimary = place?.googlePlaceIdData?.categories?.length > 0 ? place?.googlePlaceIdData?.categories?.filter((category: any) => category?.name in config?.iconMap)[0] : undefined; // category?.primary === true || 
    const placeIconUrl = place?.placeInfoIconUrl || (placeCategoryIconPrimary?.name && config?.iconMap[placeCategoryIconPrimary?.name] ? config?.iconMap[placeCategoryIconPrimary?.name]?.url : undefined) || 'https://vmajtfunkcztveldukoz.supabase.co/storage/v1/object/public/ugeo-app-images/managed/places/general.png';
    const proxyIconUrl = `/api/proxy-image?url=${encodeURIComponent(placeIconUrl)}`;
    const placeType = placeCategoryIconPrimary?.name || place?.locationType || 'Place';
    // console.log({
    //   mapPlaceToLocationMarker: place,
    //   placeCategoryIconPrimary,
    //   placeType,
    //   proxyIconUrl,
    //   placeIconUrl
    // })
    return {
      id: place?.id,
      type: 'place',
      title: place?.name,
      description: place?.placeInfoDescription,
      lat: place?.latitude || place?.locationCoordinates?.latitude,
      lng: place?.longitude || place?.locationCoordinates?.longitude,
      location: place?.locationInfoAddressLine1,
      eventDate: null,
      eventTime: null,
      eventTimestamp: null,
      priority: 1,
      placeIdUrl: place?.googlePlaceIdUrl,
      refUrl: null,
      searchable: [place?.name, place?.placeInfoDescription, `icon:${placeType}`],
      media: mediaData,
      // metaJson: place?.metaJson,
      views: place?.metaJson?.track?.views || 0,
      likes: place?.socialLikeCount || 0,
      comments: place?.socialCommentCount || 0,
      icon: {
        url: proxyIconUrl as any,
        name: placeType
      }
    };
  };
  useEffect(() => {
    if (activityId || placeId) return;
    // Use a ref to store the last serialized version of the data
    if (loading) return;
    const selectData = data?.getAnonMapFeed?.geoDrops;
    const selectPlaceData = data?.getAnonMapFeed?.places;
    const mappedData: LocationMarker[] = [];

    // Map over the geoDrops and transform them into the desired format
    for (const drop of selectData || []) {
      if (drop?.type?.toLowerCase() === 'drop' && drop?.appDropCategory?.isEvent === true && isInputDatePastNow(drop?.displayStartDate)) {
        continue;
      }
      // console.log("metaJson:", drop?.metaJson, )
      mappedData.push(mapDropToLocationMarker(drop));
    }
    for (const place of selectPlaceData || []) {
      mappedData.push(mapPlaceToLocationMarker(place));
    }
    // Serialize the new mappedData to compare with the last version
    const newSerializedData = JSON.stringify(mappedData);

    // Compare with the last cached serialized data
    if (newSerializedData !== lastSerializedDataRef.current) {
      // Update displayed results if the data has changed
      setDisplayedResults(mappedData);

      // Update the ref with the new serialized data
      lastSerializedDataRef.current = newSerializedData;
    }
  }, [data, loading]);
  const getMapData = (allParams: URLSearchParams) => {
    // console.log("getMapData called." + allParams.toString())
    if (hasAnyValue(allParams.get('activityId'), allParams.get('placeId'))) return;
    getAnonymousViewMapData({
      variables: {
        sharingAndFilterCriteria: JSON.stringify({
          searchText: allParams.get('search'),
          showPlaces: true
          // showPlacesFromGoogle: true, // ONLY FOR TESTING PURPOSES
        }),
        boundary: {
          ...getMapBoundaries()
        } as GetAnonymousViewMapDataQueryVariables['boundary']
      }
    }).then(result => {
      if (!result?.error && !result?.loading) {
        // DEV NOTE: Primary Location (last center set)
        const mapCenter = JSON.parse(localStorage.getItem("center") as string);
        // console.log("mapCenter;;>>" + JSON.stringify(mapCenter))
        if (mapCenter) {
          geoappui.fetchCity(mapCenter?.lng, mapCenter?.lat).then((city: any) => {
            if (city?.place_name) {
              // console.log("setSearchLocation.4" + JSON.stringify({lng: activity?.lng, lat: activity?.lat}))
              setSearchLocation({
                ...searchLocation,
                location: city?.place_name,
                updateMap: false
              });
            }
          });
        }
      }
    });
  };
  const debouncedGetMapData = React.useRef(debounce(getMapData, 1000)).current; // 500ms debounce

  React.useEffect(() => {
    const dataRequestParamsJSON = JSON.stringify({
      params: params.toString(),
      boundary: getMapBoundaries()
    });
    // console.log("dataRequestParamsJSON:"+dataRequestParamsJSON)
    if (dataRequestParamsJSON !== lastSerializedRequestRef.current) {
      lastSerializedRequestRef.current = dataRequestParamsJSON;
      lastSerializedParamRef.current = params;
      debouncedGetMapData(params);
    }
  }, [params]);
  React.useEffect(() => {
    const updateValue = () => {
      const paramsLatest = new URLSearchParams(window.location.search);
      setParams(paramsLatest);
      // dropIdParam || castIdParam || activityIdParam
      const updatedActivityId = paramsLatest.get('dropId') || paramsLatest.get('castId') || paramsLatest.get('activityId');
      setActivityId(updatedActivityId);
      if (!updatedActivityId) setActivity(null);
      // placeIdParam 
      const updatedPlaceId = paramsLatest.get('placeId');
      setPlaceId(updatedPlaceId);
      if (!updatedPlaceId) setPlace(null);
    };
    updateValue();

    // Respond to router events
    const handleRouteChange = () => updateValue();
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('pushState', handleRouteChange); // custom event if needed
    window.addEventListener('replaceState', handleRouteChange); // custom event if needed

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('pushState', handleRouteChange);
      window.removeEventListener('replaceState', handleRouteChange);
    };
  }, [router, searchParams]);
  React.useEffect(() => {
    if (locationIdParam && locationIdParam !== locationIdParamLast) {
      const match = locationIdParam.match(/latlng\((-?\d+\.\d+),(-?\d+\.\d+)\)/);
      if (match) {
        const mapOpenLat = parseFloat(match[1]);
        const mapOpenLng = parseFloat(match[2]);
        const HERE_API_KEY = config?.app?.settings?.hereApiKey;
        const qParams = buildQueryString({
          apikey: HERE_API_KEY,
          at: `${mapOpenLat},${mapOpenLng}`
        });
        const HERE_API_GEOCODER_URL = "https://revgeocode.search.hereapi.com/v1/revgeocode";
        const url = `${HERE_API_GEOCODER_URL}?${qParams}`;
        axios.get(url).then(result => {
          if (result?.data?.items?.length) {
            const addressLabel = result?.data?.items[0]?.address?.label;
            if (addressLabel && addressLabel != searchLocation?.location) {
              setSearchLocation({
                ...searchLocation,
                location: addressLabel,
                mapCenter: {
                  lat: mapOpenLat,
                  lng: mapOpenLng
                },
                updateMap: !locationIdParamLast ? true : false // only on page init/load 
              });
            }
          }
        });
      } else if (locationIdParam && !searchLocation?.location) {
        setSearchLocation({
          ...searchLocation,
          location: locationIdParam,
          updateMap: !locationIdParamLast ? true : false
        });
      }
    }
  }, [locationIdParamLast]);
  React.useEffect(() => {
    if (locationIdParam && locationIdParam !== locationIdParamLast) {
      setLocationIdParamLast(locationIdParam);
    }
  }, [locationIdParam]);
  React.useEffect(() => {
    if (searchLocation?.location) {
      setParam("location", searchLocation?.location);
    } else if (!locationIdParam && !hasAnyValue(activityId, placeId)) {
      //  -> https://www.mynia.app/?search=damballa&location=Brooklyn%2C+NY%2C+USA
      const mapCenter = JSON.parse(localStorage.getItem("center") as string);
      if (mapCenter && mapCenter?.lng && mapCenter?.lat) {
        geoappui.fetchCity(mapCenter?.lng, mapCenter?.lat).then((city: any) => {
          if (city?.place_name) {
            setSearchLocation({
              ...searchLocation,
              location: city?.place_name,
              mapCenter: {
                lng: mapCenter?.lng,
                lat: mapCenter?.lat
              },
              updateMap: false
            });
          }
        });
      } else {
        getUserLocation().then(loc => {
          if (!loc) {
            setSearchLocation({
              ...searchLocation,
              location: defaultSimpleLocation,
              mapCenter: defaultSimpleLocationLatLng,
              zoomLevel: 12,
              updateMap: true
            });
          } else {
            setSearchLocation({
              ...searchLocation,
              location: `${loc?.city}, ${loc?.region}, ${loc?.country}`,
              mapCenter: {
                lat: loc?.lat,
                lng: loc?.lng
              },
              zoomLevel: 12,
              updateMap: true
            });
          }
        }).catch(() => {
          setSearchLocation({
            ...searchLocation,
            location: defaultSimpleLocation,
            mapCenter: defaultSimpleLocationLatLng,
            zoomLevel: 12,
            updateMap: true
          });
        });
      }
    }
    //  else {
    //   console.log("Using Location:" + locationIdParam);
    // }
  }, [searchLocation]);
  const viewActivity = async (idIn: string) => {
    // console.log("viewActivity called")
    const apiClient = new ApiClient();
    const result = await apiClient.post(`/track/view-activity?id=${idIn}`);
    // console.log("viewActivity-response:", result)
  };
  const viewPlace = async (idIn: string) => {
    if (`${idIn}`.startsWith('google:')) return;
    // console.log("viewPlace called")
    const apiClient = new ApiClient();
    const result = await apiClient.post(`/track/view-location?id=${idIn}`);
    // console.log("viewPlace-response:", result)
  };
  const clearSelection = () => {
    clearParam(["activityId", "castId", "dropId", "placeId"]);
    setActivityId(null);
    setActivity(null);
    setPlaceId(null);
    setPlace(null);
  };
  const scrollToTop = () => {
    const overlay = document.querySelector('.overlayRef'); // Use your overlay class name here
    if (overlay) {
      overlay.scrollTop = 0; // Scroll to the top
    }
  };
  React.useEffect(() => {
    if (!didMountRef.current || loadingActivity || !hasAnyValue(activityId, activity, dataActivity)) return;
    // console.log({
    //   activityId, 
    //   activity, 
    //   errorActivity,
    //   loadingActivity,
    //   dataActivity
    // })
    if (!activityId || activityId && !activity) {
      if (activityId && !activity) {
        const apiActivityData = dataActivity?.geoActivities?.nodes[0];
        if (!apiActivityData) {
          // console.log("call api to refresh by id")
          getAnonGeoActivityByActivityId({
            variables: {
              id: activityId
            }
          });
        } else if (!activity?.id) {
          // console.log("mapDropToLocationMarker(apiActivityData):::", mapDropToLocationMarker(apiActivityData))
          setActivity(mapDropToLocationMarker(apiActivityData));
        }
      }
      // else {
      //   clearSelection()
      // }
    } else {
      setParam("activityId", activityId);
      if (activity?.id) {
        if (activity?.location) {
          geoappui.fetchCity(activity?.lng, activity?.lat).then((city: any) => {
            if (city?.place_name) {
              setSearchLocation({
                ...searchLocation,
                location: city?.place_name,
                zoomLevel: 12,
                mapCenter: {
                  lng: activity?.lng,
                  lat: activity?.lat
                },
                updateMap: true
              });
            }
          });
        }
        viewActivity(activity?.id);
        scrollToTop();
      }
    }
  }, [activityId, activity, dataActivity]);
  const getPlaceDetails = async (googlePlaceId: string) => {
    let results = {};
    const url = `/public/place-details-v2?${buildQueryString({
      googlePlaceId: googlePlaceId,
      fallbackLat: 'fallbackLat',
      fallbackLng: 'fallbackLng',
      fallbackAddress: 'fallbackAddress',
      fallbackName: 'fallbackName'
    })}`;
    const apiClient = new ApiClient();
    const placeDetailResponse: any = await apiClient.get(url);
    // console.log("placeDetailResponse::", placeDetailResponse)
    if (placeDetailResponse.status === 'success' && placeDetailResponse?.message?.id != placeId) {
      setPlaceId(placeDetailResponse?.message?.id);
    }
  };
  React.useEffect(() => {
    if (!didMountRef.current || loadingPlace || !hasAnyValue(placeId, place, dataPlace)) return;
    // console.log({
    //   placeId, 
    //   place, 
    //   errorPlace,
    //   loadingPlace,
    //   dataPlace
    // })
    if (!placeId || placeId && !place) {
      if (placeId && !place) {
        // console.log("got placeId but not data")
        const apiPlaceData = dataPlace?.geoCodes?.nodes[0];
        if (!apiPlaceData) {
          if (`${placeId}`.startsWith('google:')) {
            // console.log("call api to refresh by google place id")
            const googlePlaceId = `${placeId}`.replace("google:", "");
            getPlaceDetails(googlePlaceId);
          } else {
            // console.log("call api to refresh by geocode id")
            loadAnonGeoCode({
              variables: {
                geoCodeId: placeId
              }
            });
          }
        } else if (!place?.id) {
          // console.log(" no place?.id -----")
          setPlace(mapPlaceToLocationMarker(apiPlaceData));
        }
      }
      // else {
      //   console.log("clearing selection")
      //   clearSelection()
      // }
    } else {
      setParam("placeId", placeId);
      if (place?.id) {
        if (place?.location) {
          geoappui.fetchCity(place?.lng, place?.lat).then(city => {
            if (city?.place_name) {
              setSearchLocation({
                ...searchLocation,
                location: city?.place_name,
                zoomLevel: 12,
                mapCenter: {
                  lng: place?.lng,
                  lat: place?.lat
                },
                updateMap: true
              });
            }
          });
        }
        viewPlace(place?.id);
        scrollToTop();
      }
    }
  }, [placeId, place, dataPlace]);
  const calculateDynamicOffsetY = (offsetYTarget = 300) => {
    if (mapRef.current) {
      const mapHeight = mapRef.current.getContainer().clientHeight;
      // Calculate the dynamic offset by subtracting half the map's height from the target offset
      const dynamicOffsetY = mapHeight / 2 - offsetYTarget;
      return dynamicOffsetY;
    }
    return 0; // Default offset in case mapRef is not available
  };
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };
  const isLoading = loading || loadingActivity || loadingPlace;
  return <geoappui.MapboxMap ref={mapRef} isMobile={isMobile} location={searchLocation} setLocation={setSearchLocation} setActivity={setActivity} setActivityId={setActivityId} setPlace={setPlace} setPlaceId={setPlaceId} refreshData={() => {
    const paramsToRef = lastSerializedParamRef.current || params;
    // console.log("refresh-data called:"+paramsToRef.toString())
    debouncedGetMapData(paramsToRef);
  }} focus={activity?.id ? {
    type: 'activity',
    content: activity
  } : place?.id ? {
    type: 'place',
    content: place
  } : undefined} displayedResults={searchParam == null || searchParam != null && !!searchParam ? displayedResults || [] : []} displayedResultsFilter={selectedIcon ? `icon:${selectedIcon}` : null} addCenterOffset={!isMobile && (displayedResults || activity?.id || place?.id) ? {
    offsetX: -120,
    offsetY: calculateDynamicOffsetY()
  } : null} initCenter={(() => {
    const mapCenter = JSON.parse(localStorage.getItem("center") as string);
    if (mapCenter && mapCenter?.lng && mapCenter?.lat) {
      return [mapCenter?.lng, mapCenter?.lat];
    }
    return [defaultSimpleLocationLatLng?.lng, defaultSimpleLocationLatLng?.lat]; // NYC, NY
  })()} data-sentry-element="unknown" data-sentry-component="LocalSearch" data-sentry-source-file="LocalSearch.tsx">
      {<>
          <div className="flex">
            <div className="">

              {isLoading ? <div className="pl-4 pb-2"><p>loading...</p></div> : <></>}

              <div className={"px-4"}>

                {activityId || placeId ? <BackButton onBack={() => {
              clearSelection();
            }} header={displayedResults ? "Results" : "Go Back"} /> : <></>}
              </div>

              {isMobile && !(activityId || placeId) && !showProfileSection ? <div className="flex items-start">
                  <>
                    <ListMapToggle onToggle={(isMapMode: boolean) => {
                setIsMapMode(isMapMode);
              }} />
                  </>
                  {isEditing ? <>
                      <LocationSelector apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''} hideHeader={true} initEditMode={true} isEditingCallback={setIsEditing} />
                    </> : <>
                      <p className="font-bold pl-2 pt-2">{searchLocation?.location}</p> 
                    </>}
                  {<FiEdit size={isEditing ? 30 : 20} className={"text-gray-500 cursor-pointer ml-4 mt-2" + (isEditing ? "mb-2" : "")} onClick={toggleEditMode} />}
                </div> : <></>}
              {!isMobile ? <LocationSelector apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''} /> : <></>}

              <>
                {showProfileSection ? <GroupProfile /> : <></>}
              </>

              {activityId || placeId || loadingPlace || loadingActivity ? <ContentCard content={{
            ...(activityId ? activity : undefined),
            ...(placeId ? place : undefined)
          }} onLocationClick={(resultId, result) => {
            // console.log({
            //   resultId,
            //   result
            // })
            if (result?.type === 'drop') {
              setActivityId(resultId);
              setActivity(result);
            } else if (result?.type === 'place') {
              setPlaceId(resultId);
              setPlace(result);
            }
          }} /> : displayedResults?.length > 0 && (!isMobile || !isMapMode) && searchLocation?.location && (searchParam == null || searchParam != null && !!searchParam) ? <SearchResults displayedResults={displayedResults} header={searchParam == null ? "Popular" : "Search Results"} onResultView={activityId ? viewActivity : placeId ? viewPlace : undefined} onResultClick={(resultId, result) => {
            // console.log({
            //   resultId,
            //   result
            // })
            if (result?.type === 'drop') {
              setActivityId(resultId);
              setActivity(result);
            } else if (result?.type === 'place') {
              setPlaceId(resultId);
              setPlace(result);
            }
            // if (mapRef.current) {
            //   mapRef.current.zoomTo(16)
            // }
          }} /> : <></>}
            </div>
          </div>
        </>}
    </geoappui.MapboxMap>;
};
export default LocalSearch;